import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import apiService from '../services/ApiService';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../contexts/ToastContext';
import PageHeader, { RefreshButton } from '../components/shell/PageHeader';
import UserToolsModal from '../components/users/UserToolsModal';
import BulkApprovalModal from '../components/users/BulkApprovalModal';
import {
    APPROVAL_BADGE, APPROVAL_LABEL, PERSONA_LABEL,
    type AdminUserListItem, type ApprovalStatus,
} from '../types/users';
import { formatDateTime, formatRelative, initialsOf } from '../utils/format';
import '../styles/users.scss';

/**
 * Elenco utenti. I filtri stanno nell'URL (/users?approval=Pending&q=…),
 * cosi' dashboard e palette comandi possono portare direttamente a una vista.
 * Le righe si selezionano per approvare, sospendere o rifiutare in blocco;
 * il clic su una riga apre la scheda utente.
 */
const APPROVAL_TABS: { value: 'all' | ApprovalStatus; label: string }[] = [
    { value: 'Pending', label: 'In attesa' },
    { value: 'Approved', label: 'Approvati' },
    { value: 'Suspended', label: 'Sospesi' },
    { value: 'Rejected', label: 'Rifiutati' },
    { value: 'all', label: 'Tutti' },
];

const UsersPage: React.FC = () => {
    const { user: me } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [users, setUsers] = useState<AdminUserListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [toolUser, setToolUser] = useState<AdminUserListItem | null>(null);
    const [approveMode, setApproveMode] = useState(false);
    const [bulkAction, setBulkAction] = useState<ApprovalStatus | null>(null);

    const approval = (params.get('approval') ?? 'Pending') as 'all' | ApprovalStatus;
    const role = params.get('role') ?? 'all';
    const search = params.get('q') ?? '';
    const onlineOnly = params.get('online') === '1';

    const setParam = (key: string, value: string, fallback: string) => {
        const next = new URLSearchParams(params);
        if (value === fallback) next.delete(key); else next.set(key, value);
        setParams(next, { replace: true });
        setSelected(new Set());
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setUsers(await apiService.adminGetUsers<AdminUserListItem>());
        } catch {
            showToast('Errore', 'Impossibile caricare gli utenti', 'danger');
        } finally {
            setLoading(false);
        }
    }, [showToast]);
    useEffect(() => { void load(); }, [load]);

    const counts = useMemo(() => {
        const c: Record<string, number> = { all: users.length };
        users.forEach(u => { c[u.approvalStatus] = (c[u.approvalStatus] ?? 0) + 1; });
        return c;
    }, [users]);
    const onlineCount = users.filter(u => u.isOnline).length;

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users.filter(u =>
            (approval === 'all' || u.approvalStatus === approval) &&
            (role === 'all' || u.role === role) &&
            (!onlineOnly || u.isOnline) &&
            (!q || `${u.email} ${u.displayName ?? ''} ${u.companyName ?? ''} ${u.persona ?? ''}`.toLowerCase().includes(q)));
    }, [users, approval, role, search, onlineOnly]);

    const selectedUsers = users.filter(u => selected.has(u.id));
    const allSelected = filtered.length > 0 && filtered.every(u => selected.has(u.id));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map(u => u.id)));
    const toggleOne = (id: number) => setSelected(s => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const merge = (updated: AdminUserListItem[]) =>
        setUsers(list => list.map(u => {
            const x = updated.find(y => y.id === u.id);
            // L'aggiornamento non porta i campi dell'elenco (presenza, tool): si conservano.
            return x ? { ...u, ...x, isOnline: u.isOnline, enabledToolCount: u.enabledToolCount, lastActivityAt: u.lastActivityAt, companyName: u.companyName } : u;
        }));

    const setApproval = async (u: AdminUserListItem, status: ApprovalStatus) => {
        try {
            const updated = await apiService.adminSetUserApproval<AdminUserListItem>(u.id, status);
            merge([updated]);
            showToast('Stato aggiornato', `${u.email}: ${APPROVAL_LABEL[status]}`, 'success');
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Aggiornamento non riuscito', 'danger');
        }
    };

    const setRole = async (u: AdminUserListItem, newRole: 'User' | 'Admin') => {
        try {
            await apiService.adminSetUserRole(u.id, newRole);
            showToast('Ruolo aggiornato', `${u.email} è ora ${newRole === 'Admin' ? 'amministratore' : 'utente'}`, 'success');
            await load();
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Aggiornamento ruolo non riuscito', 'danger');
        }
    };

    const openTools = (u: AdminUserListItem, approve: boolean) => { setApproveMode(approve); setToolUser(u); };

    return (
        <>
            <PageHeader
                title="Utenti"
                description="Approvazione delle registrazioni, ruoli applicativi e accesso ai tool."
                meta={!loading && <>
                    <span><strong className="text-body">{users.length}</strong> utenti</span>
                    <span><strong className="text-body">{counts.Pending ?? 0}</strong> in attesa</span>
                    <button type="button" className={`users-online-chip ${onlineOnly ? 'is-active' : ''}`}
                        onClick={() => setParam('online', onlineOnly ? '0' : '1', '0')}
                        title="Mostra solo chi ha il portale aperto ora">
                        <i aria-hidden="true" /><strong>{onlineCount}</strong> online ora
                    </button>
                </>}
                actions={<RefreshButton onClick={load} loading={loading} />}
            />

            <div className="users-toolbar">
                <div className="et-segmented" role="tablist" aria-label="Stato di approvazione">
                    {APPROVAL_TABS.map(t => (
                        <button key={t.value} type="button" role="tab" aria-selected={approval === t.value}
                            className={approval === t.value ? 'is-active' : ''}
                            onClick={() => setParam('approval', t.value, 'Pending')}>
                            {t.label}<span className="seg-count">{counts[t.value] ?? 0}</span>
                        </button>
                    ))}
                </div>
                <select className="form-select form-select-sm w-auto" aria-label="Ruolo"
                    value={role} onChange={e => setParam('role', e.target.value, 'all')}>
                    <option value="all">Tutti i ruoli</option>
                    <option value="Admin">Amministratori</option>
                    <option value="User">Utenti</option>
                </select>
                <div className="filter-bar__search users-toolbar__search">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input type="search" className="form-control form-control-sm" placeholder="Cerca email, nome, azienda…"
                        aria-label="Cerca utenti" value={search} onChange={e => setParam('q', e.target.value, '')} />
                </div>
            </div>

            {selected.size > 0 && (
                <div className="users-bulkbar" role="region" aria-label="Azioni sui selezionati">
                    <strong>{selected.size} {selected.size === 1 ? 'selezionato' : 'selezionati'}</strong>
                    <button className="btn btn-link btn-sm" onClick={() => setSelected(new Set())}>Deseleziona</button>
                    <span className="flex-grow-1" />
                    <button className="btn btn-mint btn-sm" onClick={() => setBulkAction('Approved')}>
                        <i className="bi bi-check2-circle me-1" />Approva
                    </button>
                    <button className="btn btn-ghost-light btn-sm" onClick={() => setBulkAction('Suspended')}>
                        <i className="bi bi-pause-circle me-1" />Sospendi
                    </button>
                    <button className="btn btn-ghost-light btn-sm" onClick={() => setBulkAction('Rejected')}>
                        <i className="bi bi-x-circle me-1" />Rifiuta
                    </button>
                </div>
            )}

            <div className="card shadow-sm">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 users-table">
                        <thead>
                            <tr>
                                <th className="users-table__check">
                                    <input type="checkbox" className="form-check-input m-0" aria-label="Seleziona tutti"
                                        checked={allSelected} onChange={toggleAll} disabled={filtered.length === 0} />
                                </th>
                                <th>Utente</th>
                                <th>Stato</th>
                                <th>Profilo</th>
                                <th className="text-end">Tool</th>
                                <th>Ultima attività</th>
                                <th>Registrato</th>
                                <th className="text-end">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="text-center text-body-secondary p-4">
                                    <span className="spinner-border spinner-border-sm me-2" />Caricamento…
                                </td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-body-secondary p-5">
                                    {approval === 'Pending' && !search
                                        ? <><i className="bi bi-check2-all d-block fs-3 mb-2 text-success" />Nessuna registrazione in attesa.</>
                                        : 'Nessun utente con questi filtri.'}
                                </td></tr>
                            )}
                            {!loading && filtered.map(u => {
                                const isMe = u.email === me?.email;
                                return (
                                    <tr key={u.id} role="button" className={selected.has(u.id) ? 'is-selected' : ''}
                                        onClick={() => navigate(`/users/${u.id}`)}>
                                        <td className="users-table__check" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox" className="form-check-input m-0" aria-label={`Seleziona ${u.email}`}
                                                checked={selected.has(u.id)} onChange={() => toggleOne(u.id)} />
                                        </td>
                                        <td>
                                            <div className="users-table__user">
                                                <span className={`user-avatar ${u.isOnline ? 'is-online' : ''}`}
                                                    title={u.isOnline ? 'Online ora' : undefined}>
                                                    {initialsOf(u.email, u.displayName)}
                                                </span>
                                                <span className="users-table__id">
                                                    <strong>
                                                        {u.displayName || u.email}
                                                        {isMe && <span className="badge bg-info ms-2">tu</span>}
                                                        {u.role === 'Admin' && <span className="badge bg-primary ms-2">Admin</span>}
                                                    </strong>
                                                    <small>{u.displayName ? u.email : ''}{u.companyName ? `${u.displayName ? ' · ' : ''}${u.companyName}` : ''}</small>
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge ${APPROVAL_BADGE[u.approvalStatus] ?? 'bg-secondary'}`}>
                                                {APPROVAL_LABEL[u.approvalStatus] ?? u.approvalStatus}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="small">{PERSONA_LABEL[u.persona ?? ''] ?? u.persona ?? '—'}</div>
                                            {!u.profileCompleted && <small className="text-warning-emphasis">Profilo da completare</small>}
                                        </td>
                                        <td className="text-end tabular">{u.enabledToolCount ?? '—'}</td>
                                        <td className="small" title={formatDateTime(u.lastActivityAt ?? u.lastLoginAt)}>
                                            {u.isOnline ? <span className="text-success fw-semibold">Online ora</span>
                                                : formatRelative(u.lastActivityAt ?? u.lastLoginAt)}
                                        </td>
                                        <td className="small text-body-secondary" title={formatDateTime(u.createdAt)}>{formatRelative(u.createdAt)}</td>
                                        <td className="text-end" onClick={e => e.stopPropagation()}>
                                            <div className="d-inline-flex align-items-center gap-1">
                                                {u.approvalStatus === 'Pending' && (
                                                    <button className="btn btn-primary btn-sm" onClick={() => openTools(u, true)}>Approva</button>
                                                )}
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle as="button" className="btn btn-outline-secondary btn-sm users-table__more" aria-label="Altre azioni">
                                                        <i className="bi bi-three-dots" />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => navigate(`/users/${u.id}`)}><i className="bi bi-person-lines-fill me-2" />Apri scheda</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => openTools(u, false)}><i className="bi bi-boxes me-2" />Tool abilitati…</Dropdown.Item>
                                                        <Dropdown.Divider />
                                                        {u.approvalStatus !== 'Approved' && u.approvalStatus !== 'Pending' && (
                                                            <Dropdown.Item onClick={() => void setApproval(u, 'Approved')}><i className="bi bi-arrow-counterclockwise me-2" />Riattiva</Dropdown.Item>
                                                        )}
                                                        {u.approvalStatus === 'Approved' && !isMe && (
                                                            <Dropdown.Item onClick={() => void setApproval(u, 'Suspended')}><i className="bi bi-pause-circle me-2" />Sospendi</Dropdown.Item>
                                                        )}
                                                        {u.approvalStatus === 'Pending' && (
                                                            <Dropdown.Item onClick={() => { setSelected(new Set([u.id])); setBulkAction('Rejected'); }}>
                                                                <i className="bi bi-x-circle me-2" />Rifiuta…
                                                            </Dropdown.Item>
                                                        )}
                                                        {u.role === 'User'
                                                            ? <Dropdown.Item onClick={() => void setRole(u, 'Admin')}><i className="bi bi-shield-lock me-2" />Rendi amministratore</Dropdown.Item>
                                                            : !isMe && <Dropdown.Item onClick={() => void setRole(u, 'User')}><i className="bi bi-shield-x me-2" />Togli ruolo admin</Dropdown.Item>}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <UserToolsModal
                user={toolUser}
                approve={approveMode}
                onClose={() => setToolUser(null)}
                onSaved={updated => {
                    setToolUser(null);
                    if (updated) merge([updated]);
                    void load();
                }}
            />

            <BulkApprovalModal
                users={selectedUsers}
                action={bulkAction}
                onClose={() => setBulkAction(null)}
                onDone={updated => {
                    setBulkAction(null);
                    setSelected(new Set());
                    merge(updated);
                    void load();
                }}
            />
        </>
    );
};

export default UsersPage;
